import { forwardRef } from 'react';

import { FormattedMessage } from '@zenbusiness/application-commons-intl';
import Divider from '@zenbusiness/zui/Divider';
import Link from '@zenbusiness/zui/Link';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';

import * as Styled from './BuyBox.style';
import { BuyBoxProvider, useBuyBoxContext } from './BuyBoxContext';
import CTAButton from './CTAButton';
import Features from './Features';
import TitleSection from './TitleSection';
import VeloBanner from './VeloBanner';

import { useVeloBannerFeatureFlag } from '../../utils/veloBannerFlags';
import { Callout } from '../Callout';
import { ComparePackagesModal } from '../ComparePackagesModal';

const BuyBoxContent = forwardRef(function BuyBoxContent(props, ref) {
  const {
    note,
    packages,
    packageActive,
    comparePackages,
    plans,
    bulletPoints,
    packageNote,
    productDescription,
    packageLabel,
    packageColor,
    displayCallout,
    displayCouponBanner,
    couponBannerMessage,
    termsLink,
    veloBanner,
    handleTabChange,
    handleComparePackagesClick,
    textLink,
    handleTextLinkClick,
  } = useBuyBoxContext();

  const isVeloAmendmentSlideOutEnabled = useVeloBannerFeatureFlag();

  return (
    <Styled.BuyBoxWrapper>
      <Styled.BuyBoxContainer ref={ref}>
        {packages.length > 1 && (
          <Styled.TabsContainer
            onChange={handleTabChange}
            scrollButtons="auto"
            value={packageActive}
            variant="scrollable"
          >
            {packages.map((pkg, index) => {
              const active = packageActive === index;
              return (
                <Styled.TabButton
                  active={packageActive.toString()}
                  key={pkg.packageName}
                  label={
                    <Stack>
                      <Text color={active ? 'white' : 'primary'}>
                        <FormattedMessage id={pkg.packageName} />
                      </Text>
                      {pkg.packageNote && (
                        <Text color={active ? 'white' : 'primary'} size="xs">
                          <FormattedMessage id={pkg.packageNote} />
                        </Text>
                      )}
                    </Stack>
                  }
                  selected={active}
                  value={index}
                />
              );
            })}
          </Styled.TabsContainer>
        )}

        <Styled.BuyBox note={packageNote} color={packageColor}>
          {packageNote && (
            <Text
              color="primary"
              size="xs"
              textTransform="uppercase"
              weight="medium"
              sx={{ alignContent: 'center', textAlign: 'center', height: '1.75rem' }}
            >
              <FormattedMessage id={packageLabel || packageNote} />
            </Text>
          )}
          <Styled.BuyBoxContent>
            <TitleSection />
            <Divider />
            {productDescription && (
              <Text color="primary" weight="medium">
                <FormattedMessage id={productDescription} />
              </Text>
            )}
            <Features features={bulletPoints} />
            {displayCallout && <Callout title="productPage.buyBox.purchasedCallout.title" variant="info" />}
            {displayCouponBanner && (
              <Styled.StyledCouponBanner
                title=""
                subtitle={<FormattedMessage id={couponBannerMessage} />}
                variant="promo"
              />
            )}
            {veloBanner?.displayVeloBanner && isVeloAmendmentSlideOutEnabled && (
              <VeloBanner prompt={veloBanner.prompt} message={veloBanner.message} queryType={veloBanner.queryType} />
            )}
            <CTAButton />
            {termsLink && (
              <Stack alignItems="flex-end">
                <Link href={termsLink.url} size="sm" color="info" textDecoration="none">
                  <FormattedMessage id={termsLink.message} />
                </Link>
              </Stack>
            )}
            {note && (
              <Styled.SmallCopyText>
                <FormattedMessage id={note} />
              </Styled.SmallCopyText>
            )}
            {textLink && (
              <Styled.ButtonLink onClick={handleTextLinkClick} size="sm" variant="text" color="info" p={0}>
                <FormattedMessage id={textLink} />
              </Styled.ButtonLink>
            )}
            {comparePackages && (
              <Styled.ButtonLinkContainer>
                <Styled.ButtonLink
                  onClick={handleComparePackagesClick}
                  size="sm"
                  variant="text"
                  color="info"
                  sx={{ padding: 0 }}
                >
                  <FormattedMessage id={comparePackages} />
                </Styled.ButtonLink>
              </Styled.ButtonLinkContainer>
            )}
            {plans && <ComparePackagesModal plans={plans} />}
          </Styled.BuyBoxContent>
        </Styled.BuyBox>
      </Styled.BuyBoxContainer>
    </Styled.BuyBoxWrapper>
  );
});

const BuyBox = forwardRef(function BuyBox(props, ref) {
  return (
    <BuyBoxProvider>
      <BuyBoxContent ref={ref} />
    </BuyBoxProvider>
  );
});

export default BuyBox;
